import { useCallback } from 'react';
import { useRefreshDmMessagesInbox } from '../../../../hooks/useDmMessagesInbox';
import type { DmLastResolution } from '../../../../services/dmNotifications';
import { MarkAllReviewedButton } from '../../../monitoring/messages/MarkAllReviewedButton';
import type { AiReplySource } from '../../../monitoring/messages/triage/aiTriageState';
import { PatientMessagesLanding } from './PatientMessagesLanding';

interface DmPatientMessagesPanelProps {
    patientId: string;
    unreviewedCount?: number | null;
    lastResolution?: DmLastResolution | null;
    aiReply?: AiReplySource;
    onReviewed?: (patientId: string) => Promise<void>;
    variant?: 'outline' | 'primary';
    // Skip the "Messages" bar when the host already shows the button in its own header
    // (the monitoring slide-over's patient header).
    showHeader?: boolean;
    embedded?: boolean;
    onBack?: () => void;
}

// Composes mark-as-read, the DM thread and its AI reply the same way everywhere they're shown.
// Sending also refreshes the inbox: the backend marks messages reviewed after a send.
export function DmPatientMessagesPanel({
    patientId,
    unreviewedCount = null,
    lastResolution = null,
    aiReply,
    onReviewed,
    variant = 'outline',
    showHeader = true,
    embedded = false,
    onBack
}: DmPatientMessagesPanelProps) {
    const refreshInbox = useRefreshDmMessagesInbox();
    const onMessageSent = useCallback(() => void refreshInbox(), [refreshInbox]);

    const markReviewedButton = showHeader && (
        <MarkAllReviewedButton
            patientId={patientId}
            unreviewedCount={unreviewedCount}
            lastResolution={lastResolution}
            onReviewed={onReviewed}
            variant={variant}
        />
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* Embedded hosts have no back header: show the "Messages" bar here. Others get one
                from PatientMessagesLanding, so the button goes in its header-right slot instead. */}
            {embedded && markReviewedButton && (
                <header className="flex flex-shrink-0 items-center gap-2 border-b border-gray-100 px-5 py-3">
                    <h2 className="text-[13px] font-semibold text-gray-800">Messages</h2>
                    <div className="ml-auto">{markReviewedButton}</div>
                </header>
            )}
            <PatientMessagesLanding
                patientId={patientId}
                embedded={embedded}
                onBack={onBack}
                onMessageSent={onMessageSent}
                aiReply={aiReply}
                headerRight={!embedded ? markReviewedButton : undefined}
            />
        </div>
    );
}
